import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../../../interfaces/form.interface';
import { DropdownData } from '../../../interfaces/input.interface';
import {
    AgriculturalFieldHelpDetails,
    AgriculturalHelpContext,
    buildAgriculturalFieldHelp,
    buildAgriculturalFieldHelpDetails,
} from 'src/app/common/components/form-field-help/form-field-help.util';

@Component({
    selector: 'app-reactive-field-label',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './field-label.component.html',
    styleUrls: ['./field-label.component.scss'],
})
export class ReactiveFieldLabelComponent<T> implements OnChanges {
    @Input() field!: FormField<T>;

    @Input() form!: Form<T>;

    @Input() model!: FormGroup;

    @Input() originalModel: Record<string, string> = {};

    @Input() undoEnabled = false;

    @Input() tourAnchorId = '';

    @Input() sectionName = '';

    @Input() showFieldHelpButtons = false;
    
    @Output() undoRequested = new EventEmitter<FormField<T>>();

    public isHelpExpanded = false;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['showFieldHelpButtons'] && !this.showFieldHelpButtons) {
            this.isHelpExpanded = false;
        }
    }

    

    public isFieldEdited(): boolean {
        const originalValue = this.originalModel[this.field.name ?? ''];
        const currentValue = this.model.get(this.field.name ?? '')?.value;

        return (this.field.conditions.hasUndoEnabled || this.undoEnabled) && currentValue !== originalValue;
    }

    public sum(): string {
        if (this.form.type >= 3) {
            return '';
        }

        const selectedItems = ((this.field.input.data as DropdownData<T>)?.selected ?? []) as Record<string, string>[];
        const summation = this.field.input?.dropdownConfiguration?.summation;

        if (!summation) {
            return '';
        }

        const total = selectedItems.reduce(
            (currentTotal, value) => currentTotal + parseFloat(value?.[summation.summand]?.replace(',', '.') || '0.00'),
            0
        );
        const formatter = summation.format;
        const totalFormatted = typeof formatter === 'function' ? formatter(total) : total.toFixed(2);

        return `(${totalFormatted} ${summation.uds})`;
    }


    public getFieldHelpText(): string {
        return buildAgriculturalFieldHelp(this.field.name, this.field.label, this.getExplicitHelpText(), this.getHelpContext());
    }

    public getFieldHelpDetails(): AgriculturalFieldHelpDetails {
        return buildAgriculturalFieldHelpDetails(this.field.name, this.field.label, this.getExplicitHelpText(), this.getHelpContext());
    }

    public getHelpPanelId(): string {
        return `field-help-${this.field.name ?? 'unknown'}`;
    }

    public toggleFieldHelp(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isHelpExpanded = !this.isHelpExpanded;
    }

    public undo(): void {
        this.undoRequested.emit(this.field);
    }

    private getHelpContext(): AgriculturalHelpContext {
        return {
            ...(this.form?.name !== undefined ? { formName: this.form.name } : {}),
            ...(this.form?.header !== undefined ? { formHeader: this.form.header } : {}),
            ...(this.sectionName ? { sectionName: this.sectionName } : {}),
        };
    }

    private getExplicitHelpText(): string | undefined {
        const helpText = this.field['helpText'];

        return typeof helpText === 'string' ? helpText : undefined;
    }
}
